"use client"

/**
 * Тонкий theme provider — заміна next-themes (який кидав console warning
 * у Next 16 / React 19 через інжектований <script> у Provider tree).
 *
 * Експортує API сумісний з next-themes:
 *   useTheme() → { theme, setTheme }
 * щоб call sites (theme-toggle, settings) не змінювались.
 *
 * Стан зберігаємо у localStorage["theme"]. Тогл — class "dark" на <html>.
 * SSR-flash protection не потрібен бо defaultTheme="light" і
 * enableSystem=false — тема детермінована до hydration.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"

type Theme = "light" | "dark"

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  setTheme: () => {},
})

const STORAGE_KEY = "theme"

function applyToHtml(theme: Theme): void {
  if (typeof document === "undefined") return
  const root = document.documentElement
  if (theme === "dark") root.classList.add("dark")
  else root.classList.remove("dark")
}

function readStored(): Theme {
  if (typeof window === "undefined") return "light"
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    if (v === "dark" || v === "light") return v
  } catch {
    /* ignore */
  }
  return "light"
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("light")

  // Hydrate from localStorage post-mount. SSR sends "light" (defaultTheme),
  // and ми перемикаємо у browser якщо у юзера збережено dark.
  useEffect(() => {
    const stored = readStored()
    setThemeState(stored)
    applyToHtml(stored)
  }, [])

  const setTheme = (next: Theme) => {
    setThemeState(next)
    applyToHtml(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext)
}
